import { useState, useRef, useCallback, useEffect } from 'react';
import axiosService from 'utils/axios';

const POLL_INTERVAL_MS = 1000;

export interface ImportRowError {
  row: number | null;
  vin: string;
  stock: string;
  field: string;
  message: string;
}

export interface ImportResultSummary {
  totalRows: number;
  created: number;
  updated: number;
  skipped: number;
  errors: ImportRowError[];
}

export interface VehicleImportStatus {
  id: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  progress: number;
  errorMessage: string | null;
  resultSummary: ImportResultSummary;
}

interface UseVehicleImportReturn {
  isImporting: boolean;
  importProgress: number;
  importResult: VehicleImportStatus | null;
  startImport: (file: File) => void;
  clearResult: () => void;
}

interface VehicleImportResultSummaryResponse {
  total_rows?: number;
  created?: number;
  updated?: number;
  skipped?: number;
  errors?: ImportRowError[];
}

interface VehicleImportResponse {
  id: string;
  status: VehicleImportStatus['status'];
  progress: number;
  error_message: string | null;
  result_summary: VehicleImportResultSummaryResponse | null;
}

const emptySummary = (): ImportResultSummary => ({
  totalRows: 0,
  created: 0,
  updated: 0,
  skipped: 0,
  errors: [],
});

const mapResultSummary = (raw: VehicleImportResultSummaryResponse | null | undefined): ImportResultSummary => {
  const errors = raw?.errors;
  return {
    totalRows: raw?.total_rows ?? 0,
    created: raw?.created ?? 0,
    updated: raw?.updated ?? 0,
    skipped: raw?.skipped ?? 0,
    errors: Array.isArray(errors) ? errors : [],
  };
};

const mapResponseToImportStatus = (data: VehicleImportResponse): VehicleImportStatus => ({
  id: data.id,
  status: data.status,
  progress: data.progress,
  errorMessage: data.error_message,
  resultSummary: mapResultSummary(data.result_summary),
});

const failedResult = (errorMessage: string, id = ''): VehicleImportStatus => ({
  id,
  status: 'failed',
  progress: 0,
  errorMessage,
  resultSummary: emptySummary(),
});

const apiErrorMessage = (err: {
  message?: string;
  response?: { data?: { error?: string; detail?: string } };
}): string => err.response?.data?.error || err.response?.data?.detail || err.message || '';

export const useVehicleImport = (): UseVehicleImportReturn => {
  const [isImporting, setIsImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [importResult, setImportResult] = useState<VehicleImportStatus | null>(null);
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopPolling = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, [stopPolling]);

  const pollImportStatus = useCallback(
    (importId: string) => {
      pollIntervalRef.current = setInterval(() => {
        axiosService
          .get(`/vehicles/import/${importId}/`)
          .then(res => {
            const result = mapResponseToImportStatus(res.data);
            setImportProgress(result.progress);

            if (result.status === 'completed' || result.status === 'failed') {
              stopPolling();
              setIsImporting(false);
              setImportResult(result);
            }
          })
          .catch(err => {
            stopPolling();
            setIsImporting(false);
            setImportResult(failedResult(apiErrorMessage(err) || 'Failed to check import status', importId));
          });
      }, POLL_INTERVAL_MS);
    },
    [stopPolling]
  );

  const startImport = useCallback(
    (file: File) => {
      setIsImporting(true);
      setImportProgress(0);
      setImportResult(null);

      const formData = new FormData();
      formData.append('file', file);

      axiosService
        .post('/vehicles/import/', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        })
        .then(res => {
          const result = mapResponseToImportStatus(res.data);
          if (result.status === 'completed' || result.status === 'failed') {
            setIsImporting(false);
            setImportResult(result);
          } else {
            pollImportStatus(result.id);
          }
        })
        .catch(err => {
          setIsImporting(false);
          setImportResult(failedResult(apiErrorMessage(err) || 'Failed to start import'));
        });
    },
    [pollImportStatus]
  );

  const clearResult = useCallback(() => {
    setImportResult(null);
    setImportProgress(0);
  }, []);

  return {
    isImporting,
    importProgress,
    importResult,
    startImport,
    clearResult,
  };
};
